import { apiGet, apiPostJson } from './client'

export function ticketPlanQuery(ticketId: number) {
  return {
    queryKey: ['easeedesk', 'ticket', ticketId, 'plan'] as const,
    queryFn: () => apiGet<Record<string, unknown>>(`/api/v2/easeedesk/tickets/${ticketId}/plan`),
  }
}

export function launchPlanStep(ticketId: number, stepId: number, body: Record<string, unknown>) {
  return apiPostJson(`/api/v2/easeedesk/tickets/${ticketId}/plan/steps/${stepId}/launch`, body)
}

export function donePlanStep(ticketId: number, stepId: number, note: string) {
  return apiPostJson(`/api/v2/easeedesk/tickets/${ticketId}/plan/steps/${stepId}/done`, { note })
}

export function skipPlanStep(ticketId: number, stepId: number, reason: string) {
  return apiPostJson(`/api/v2/easeedesk/tickets/${ticketId}/plan/steps/${stepId}/skip`, { reason })
}

export function planStepToChange(ticketId: number, stepId: number) {
  return apiPostJson<{ ok: boolean; draft_id: number; change_ticket_id: number; cab: string; message: string }>(
    `/api/v2/easeedesk/tickets/${ticketId}/plan/steps/${stepId}/to-change`,
    {},
  )
}

export function askAdamPlan(ticketId: number) {
  return apiPostJson(`/api/v2/easeedesk/tickets/${ticketId}/plan/ask-adam`, {})
}

const CONSOLE = '/api/v2/autonomy/desk'

export type DeskQueueRow = {
  run_id: number
  ticket_id: number
  company: string
  subject: string
  state: string
  request_type: string
  channel: string
  path: string
  priority: number
  rounds: number
  tier: string
  claimed_by: string
  heartbeat_age_s: number | null
  since: string
  failure_kind: string
}

export type DeskQueueFilters = { state?: string; request_type?: string; channel?: string; q?: string; page?: number }

export type DeskSla = { mode: string; elapsed: number; remaining: number; breached: boolean } | null

export type DeskEscalationRow = {
  run_id: number
  ticket_id: number
  company: string
  subject: string
  assignee: string
  handed_at: string
  handed_reason: string
  sla: DeskSla
  colleague_replied_at: string
  acknowledged: boolean
  renotifyable: boolean
}

export type DeskAgingRow = {
  run_id: number
  ticket_id: number
  company: string
  subject: string
  state: string
  age_seconds: number
  age_business_seconds: number
  sla: DeskSla
  waits_for: { kind: string }
}

export type DeskFailureDay = { day: string } & Record<string, number | string>

export type DeskHistory = {
  weeks: Array<{ week: string; taken: number; confirmed: number; held: number; silent: number; reopened: number; handed: number; cost_usd: number; cost_per_confirmed: number | null; csat: number | null }>
  by_type: Array<{ request_type: string; taken: number; rate: number | null; cost_per_confirmed: number | null }>
}

export type DeskRunPage = {
  run: Record<string, unknown> & { run_id: number; ticket_id: number; state: string; rounds: number }
  steps: Array<Record<string, unknown>>
  waits: Array<Record<string, unknown>>
  actions: Array<Record<string, unknown>>
  challenges: Array<Record<string, unknown>>
  can: { stop: boolean; handover: boolean; resume: boolean; giveback: boolean; renotify: boolean; confirm: boolean; replay: boolean }
}

export type DeskAction = 'stop' | 'resume' | 'handover' | 'replay' | 'giveback' | 'renotify' | 'confirm-waiting'

export type DeskReplayWould = { tool: string; target: string; args_keys: string[]; decision: string; reason: string }

export type DeskReplayResult = {
  ok: boolean
  step_id: number
  outcome: string
  reply: string
  cost_usd: number
  would: DeskReplayWould[]
}

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export function deskQueueQuery(filters: DeskQueueFilters) {
  return {
    queryKey: ['autonomy', 'desk', 'queue', filters] as const,
    queryFn: () => apiGet<{ rows: DeskQueueRow[]; total: number; page: number; per_page: number }>(`${CONSOLE}/queue${qs(filters)}`),
  }
}

export function deskEscalationsQuery() {
  return { queryKey: ['autonomy', 'desk', 'escalations'] as const, queryFn: () => apiGet<{ rows: DeskEscalationRow[] }>(`${CONSOLE}/escalations`) }
}

export function deskAgingQuery() {
  return { queryKey: ['autonomy', 'desk', 'aging'] as const, queryFn: () => apiGet<{ rows: DeskAgingRow[] }>(`${CONSOLE}/aging`) }
}

export function deskFailuresQuery(days = 30) {
  return {
    queryKey: ['autonomy', 'desk', 'failures', days] as const,
    queryFn: () => apiGet<{ days: DeskFailureDay[]; total: number; today: Array<Record<string, unknown>> }>(`${CONSOLE}/failures?days=${days}`),
  }
}

export function deskHistoryQuery() {
  return { queryKey: ['autonomy', 'desk', 'history'] as const, queryFn: () => apiGet<DeskHistory>(`${CONSOLE}/history`) }
}

export function deskConsoleRunQuery(runId: number) {
  return { queryKey: ['autonomy', 'desk', 'run', runId] as const, queryFn: () => apiGet<DeskRunPage>(`${CONSOLE}/runs/${runId}`) }
}

export function deskRunAction(runId: number, action: DeskAction, body: Record<string, unknown> = {}) {
  return apiPostJson<Record<string, unknown>>(`${CONSOLE}/runs/${runId}/${action}`, body)
}

export function deskEmergencyStop(reason: string) {
  return apiPostJson<{ ok: boolean; challenges: number; commands: number; runs: number; audit_id: number }>(`${CONSOLE}/emergency`, { reason })
}

export function deskWaitingQuery(companyId?: number) {
  const q = companyId ? `?company=${companyId}` : ''
  return {
    queryKey: ['autonomy', 'desk', 'waiting', companyId ?? 0] as const,
    queryFn: () => apiGet<Record<string, unknown>>(`/api/v2/autonomy/desk/waiting${q}`),
  }
}

const BOARD = '/api/v2/desk/board'

export type DeskBoardHeadline = {
  taken_month: number
  confirmed_month: number
  held_month: number
  silent_month: number
  handed_month: number
  reopens: number
  resolved_month: number
  rate: number
  cost_usd: number
  cost_per_resolved: number | null
  cost_per_confirmed: number | null
  csat: number | null
  fixed_today: number
  fixed_month: number
}

export type DeskBoardPause = { global: boolean; company: boolean; inherited: boolean; can_pause: boolean }

export type DeskBoard = {
  company: { id: number; name: string }
  month_start: string
  headline: DeskBoardHeadline
  queue: Record<string, number>
  waiting: { person: number; approval: number; vendor: number; plan: number; order: number }
  handed: Array<{
    run_id: number
    ticket_id: number
    subject: string
    request_type: string
    assignee_id: number
    assignee: string
    handed_at: string
    age_seconds: number
    reason: string
  }>
  orders: Array<{
    run_id: number
    ticket_id: number
    subject: string
    request_id: number
    age_seconds: number
    items: Array<{ name: string; quantity: number; price: number | null; currency: string }>
  }>
  plans: Array<{ run_id: number; ticket_id: number; subject: string; plan_id: number; age_seconds: number }>
  by_type: Array<{ request_type: string; taken: number; confirmed: number; resolved: number; rate: number; cost_usd: number }>
  heartbeat: { tick: string; at: string | null; stale: boolean }
  pause: DeskBoardPause
  report: { has_monthly: boolean; report_id: string | null; can_create: boolean }
}

function boardQs(companyId?: number) {
  return companyId ? `?company=${companyId}` : ''
}

export function deskBoardQuery(companyId?: number) {
  return { queryKey: ['desk', 'board', companyId ?? 0] as const, queryFn: () => apiGet<DeskBoard>(`${BOARD}${boardQs(companyId)}`) }
}

export function deskBoardPause(paused: boolean, reason: string, companyId?: number) {
  return apiPostJson<{ ok: boolean; pause: DeskBoardPause }>(`${BOARD}/pause${boardQs(companyId)}`, { paused, reason })
}

/** Saves "IT department - monthly" on the company and schedules it for the 1st; empty `to` = the caller. */
export function deskBoardMonthlyReport(to: string[], companyId?: number) {
  return apiPostJson<{ ok: boolean; report_id: string; next_run_at: string | null }>(`${BOARD}/report${boardQs(companyId)}`, { to })
}
